import { ChevronDown, Settings04 } from '@untitledui/icons'

import Button from '@components/designSystem/Button'
import { useDialog } from '@components/designSystem/Dialog/useDialog'
import { HorizontalScroller } from '@components/designSystem/HorizontalScroller'
import { Tabs } from '@components/designSystem/Tabs'
import { Badge } from '@components/designSystem/badges/badges'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useTodoListContext } from '@routes/TodoList/Context'
import FilterAndSortDialog from '@routes/TodoList/FilterAndSortDialog'
import { CheckBaseBadge, RefillBaseBadge, ReorderBaseBadge } from '@routes/TodoList/badges'
import { useTaskCountByFilter, useTodoCount } from '@state/todoList'

import type { FilterAndSort } from '@state/util/todoList'
import type { Key } from 'react-aria-components'

function FilterSection() {
  const { t } = useTypedTranslation()
  const { filterAndSort, updateFilterAndSort, setSelectedTask } = useTodoListContext()

  const { countCompleted, countOpen } = useTodoCount()
  const showFilter = countOpen !== undefined && countOpen > 0

  const tabs = [
    { id: 'OPEN', label: t('todoList.tabs.OPEN') },
    {
      id: 'COMPLETED',
      label: t('todoList.tabs.COMPLETED'),
      badge: countCompleted,
    },
  ]

  const countRefill = useTaskCountByFilter({ primaryFilter: 'REFILL' })
  const countCheck = useTaskCountByFilter({ primaryFilter: 'CHECK' })
  const countReorder = useTaskCountByFilter({ primaryFilter: 'REORDER' })

  function toggleOpenCompleted(key: Key) {
    updateFilterAndSort({ completed: key === 'COMPLETED' })
    setSelectedTask(undefined)
  }

  return (
    <Tabs selectedKey={filterAndSort.completed ? 'COMPLETED' : 'OPEN'} onSelectionChange={toggleOpenCompleted}>
      <Tabs.List type="underline" fullWidth items={tabs} className='mb-4'>
        {(tab) => <Tabs.Item className='text-md font-medium' {...tab} />}
      </Tabs.List>

      <Tabs.Panel id="OPEN">
        {showFilter && (
          <HorizontalScroller className='mb-4 grid'>
            <div className='flex items-center gap-1'>
              <FilterButton />

              <PrimaryFilterButton type='REFILL' count={countRefill} />
              <PrimaryFilterButton type='CHECK' count={countCheck} />
              <PrimaryFilterButton type='REORDER' count={countReorder} />
            </div>
          </HorizontalScroller>
        )}
      </Tabs.Panel>

      <Tabs.Panel id="COMPLETED" />
    </Tabs>
  )
}

function FilterButton() {
  const { showDialog } = useDialog(FilterAndSortDialog)
  const { filterAndSort, updateFilterAndSort } = useTodoListContext()

  function onClick() {
    showDialog({ filterAndSort, updateFilterAndSort })
  }

  return (
    <Button variant='outline' onClick={onClick} data-testid='filter-button'>
      <div className='flex'>
        <Settings04 size={20} />
        <ChevronDown size={20} />
      </div>
    </Button>
  )
}

interface PrimaryFilterButtonProps {
  type: FilterAndSort['primaryFilter']
  count?: number
}

function PrimaryFilterButton({ type, count }: PrimaryFilterButtonProps) {
  const { t } = useTypedTranslation()
  const { filterAndSort, updateFilterAndSort, setSelectedTask } = useTodoListContext()

  function clickHandler() {
    setSelectedTask(undefined)
    updateFilterAndSort({ primaryFilter: type, secondaryFilter: 'NONE', sort: 'ALPHA' })
  }

  const isSelected = filterAndSort.primaryFilter === type

  let iconTrailing = undefined
  if (count !== undefined) {
    if (isSelected) {
      if (type === 'REFILL') {
        iconTrailing = <RefillBaseBadge>{count}</RefillBaseBadge>
      } else if (type === 'CHECK') {
        iconTrailing = <CheckBaseBadge>{count}</CheckBaseBadge>
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      } else if (type === 'REORDER') {
        iconTrailing = <ReorderBaseBadge>{count}</ReorderBaseBadge>
      }
    } else {
      iconTrailing = <Badge size='sm'>{count}</Badge>
    }
  }

  return (
    <Button variant={isSelected ? 'outline' : 'gray'} iconTrailing={iconTrailing} onClick={clickHandler}>
      {t(`todoList.badges.${type}`)}
    </Button>
  )
}

export default FilterSection